import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

const buildOptionMarkers = (field) => {
  const markers = [];
  const metadata = field.metadata || {};
  const options = Array.isArray(metadata.options) ? metadata.options : [];

  options.forEach((option, index) => {
    if (option?.pdfLocation) {
      markers.push({
        key: `${field.id}-option-${index}`,
        label: option.text || option.value || `Option ${index + 1}`,
        location: option.pdfLocation,
      });
    }
  });

  if (metadata.other?.checkbox?.pdfLocation) {
    markers.push({
      key: `${field.id}-other-checkbox`,
      label: metadata.other.checkbox.text || "Other",
      location: metadata.other.checkbox.pdfLocation,
    });
  }

  if (metadata.other?.radio?.pdfLocation) {
    markers.push({
      key: `${field.id}-other-radio`,
      label: metadata.other.radio.text || "Other",
      location: metadata.other.radio.pdfLocation,
    });
  }

  if (metadata.other?.input?.pdfLocation) {
    markers.push({
      key: `${field.id}-other-input`,
      label: metadata.other.input.attributeName || "Other Input",
      location: metadata.other.input.pdfLocation,
    });
  }

  return markers;
};

export const savePdfWithAnnotations = async (
  pdfFile,
  fields,
  signatures = []
) => {
  console.log("savePdfWithAnnotations called");
  console.log("PDF File:", pdfFile);
  console.log("Fields:", fields);

  try {
    // Read the PDF file
    console.log("Reading PDF file...");
    const fileBuffer = await pdfFile.arrayBuffer();
    console.log("File buffer loaded, size:", fileBuffer.byteLength);

    const pdfDoc = await PDFDocument.load(fileBuffer);
    console.log("PDF document loaded");

    // Embed the Helvetica font
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    console.log("Font embedded");

    // Get all pages
    const pages = pdfDoc.getPages();
    console.log("Total pages:", pages.length);

    const legendItems = [];
    fields.forEach((field) => {
      const location = field.metadata?.pdfLocation;
      if (
        location?.page &&
        location.x !== undefined &&
        location.y !== undefined
      ) {
        legendItems.push({
          key: field.id,
          label: field.attributeName || field.label || field.id,
          page: location.page,
          x: location.x,
          y: location.y,
          width: location.width,
          height: location.height,
          kind: "field",
        });
      }

      buildOptionMarkers(field).forEach((marker) => {
        if (!marker.location?.page) return;
        legendItems.push({
          key: marker.key,
          label: field.attributeName
            ? `${field.attributeName} (${marker.label})`
            : marker.label,
          page: marker.location.page,
          x: marker.location.x,
          y: marker.location.y,
          width: marker.location.width,
          height: marker.location.height,
          kind: "option",
        });
      });
    });

    const orderedLegendItems = legendItems.sort(
      (a, b) => a.page - b.page || a.y - b.y || a.x - b.x
    );
    const legendIndexByKey = new Map(
      orderedLegendItems.map((item, index) => [item.key, index + 1])
    );

    console.log(
      "Fields/options with PDF locations:",
      orderedLegendItems.length
    );
    console.log("Signatures with PDF locations:", signatures.length);

    for (const item of orderedLegendItems) {
      const pageIndex = item.page - 1;
      console.log(
        `Processing ${item.kind} "${item.label}" on page ${pageIndex + 1}`
      );

      if (pageIndex >= 0 && pageIndex < pages.length) {
        const page = pages[pageIndex];
        const { width, height } = page.getSize();

        // Calculate coordinates
        // x and y are percentages (0-100)
        // Origin for percentages is top-left
        // Origin for PDF is bottom-left
        const x = (item.x / 100) * width;
        const y = height - (item.y / 100) * height;

        // Use box dimensions from metadata if available, otherwise use defaults
        const boxWidth = item.width ? (item.width / 100) * width : 100;
        const boxHeight = item.height ? (item.height / 100) * height : 20;

        // Draw a semi-transparent rectangle
        page.drawRectangle({
          x: x,
          y: y - boxHeight,
          width: boxWidth,
          height: boxHeight,
          color: rgb(1, 0, 0),
          opacity: 0.3,
          borderColor: rgb(1, 0, 0),
          borderWidth: 1,
        });

        const legendIndex = legendIndexByKey.get(item.key);
        if (legendIndex) {
          const radius = 8;
          const circleX = x + radius;
          const circleY = y - radius;
          page.drawCircle({
            x: circleX,
            y: circleY,
            size: radius,
            color: rgb(0.2, 0.55, 0.95),
            borderColor: rgb(0.1, 0.35, 0.7),
            borderWidth: 1,
          });
          page.drawText(String(legendIndex), {
            x: circleX - 3,
            y: circleY - 3,
            size: 8,
            font: helveticaFont,
            color: rgb(1, 1, 1),
          });
        }

        console.log(
          `Added annotation for "${item.label}" at (${x}, ${y}) with size ${boxWidth}x${boxHeight}`
        );
      }
    }

    for (const signature of signatures) {
      const location = signature?.pdfLocation;
      if (!location?.page) continue;
      const pageIndex = location.page - 1;

      if (pageIndex >= 0 && pageIndex < pages.length) {
        const page = pages[pageIndex];
        const { width, height } = page.getSize();

        const x = (location.x / 100) * width;
        const y = height - (location.y / 100) * height;
        const boxWidth = location.width ? (location.width / 100) * width : 100;
        const boxHeight = location.height
          ? (location.height / 100) * height
          : 20;

        page.drawRectangle({
          x: x,
          y: y - boxHeight,
          width: boxWidth,
          height: boxHeight,
          color: rgb(0.9, 0.8, 0.2),
          opacity: 0.25,
          borderColor: rgb(0.8, 0.6, 0.1),
          borderWidth: 1,
        });

        const signerKey = signature.signerKey || "";
        const text = signerKey ? `{{.sig-${signerKey}}}` : "";
        if (text) {
          page.drawText(text, {
            x: x + 2,
            y: y - boxHeight + 6,
            size: 10,
            font: helveticaFont,
            color: rgb(0, 0, 0),
          });
        }
      }
    }

    if (orderedLegendItems.length > 0) {
      const firstPage = pages[0];
      const { width, height } = firstPage.getSize();
      const margin = 50;
      let legendPage = pdfDoc.addPage([width, height]);
      let y = height - margin;

      const drawLegendHeader = (title) => {
        legendPage.drawText(title, {
          x: margin,
          y,
          size: 16,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });
        y -= 24;
      };

      drawLegendHeader("Index / Legend");

      orderedLegendItems.forEach((item, index) => {
        if (y < margin + 20) {
          legendPage = pdfDoc.addPage([width, height]);
          y = height - margin;
          drawLegendHeader("Index / Legend (continued)");
        }
        const line = `${index + 1}. ${item.label} (p${item.page})`;
        legendPage.drawText(line, {
          x: margin,
          y,
          size: 10,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        });
        y -= 14;
      });
    }

    // Serialize the PDFDocument to bytes (a Uint8Array)
    console.log("Serializing PDF...");
    const pdfBytes = await pdfDoc.save();
    console.log("PDF serialized, size:", pdfBytes.length);

    // Create the blob
    console.log("Creating download blob...");
    const blob = new Blob([pdfBytes], { type: "application/pdf" });

    try {
      // Try to use the File System Access API (Chrome/Edge/Opera)
      if ("showSaveFilePicker" in window) {
        console.log("Using showSaveFilePicker...");
        const handle = await window.showSaveFilePicker({
          suggestedName: `annotated_${pdfFile.name}`,
          types: [
            {
              description: "PDF Document",
              accept: { "application/pdf": [".pdf"] },
            },
          ],
        });

        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        console.log("File saved successfully via picker");
        alert(`PDF saved successfully!`);
      } else {
        throw new Error("File System Access API not supported");
      }
    } catch (err) {
      // Fallback for browsers that don't support the API (Firefox/Safari) or if user cancels
      if (err.name !== "AbortError") {
        console.log("Falling back to legacy download...");
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `annotated_${pdfFile.name}`;

        // Make the link visible but off-screen for better Chrome compatibility
        link.style.position = "fixed";
        link.style.left = "-9999px";
        document.body.appendChild(link);

        link.click();

        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 100);

        if (err.message === "File System Access API not supported") {
          alert(`PDF saved to your Downloads folder.`);
        }
      }
    }
  } catch (error) {
    console.error("Error saving PDF with annotations:", error);
    console.error("Error stack:", error.stack);
    alert(`Failed to save PDF with annotations: ${error.message}`);
  }
};
